import React from 'react'
import {
  DcFlexBox,
  PixelTable,
  PixelImage,
  PixelText,
  DcButton,
  PixelSpinner,
  DcText,
} from 'pixel-base'
import type { AjaxError, AjaxResponse } from 'rxjs/ajax'
import styled from 'styled-components'
import {
  updateLeadTime,
  type LeadTimeRow,
  type BulkUploadResponse,
} from 'utils/api-routes-planning'
import { ArrowLeftIcon } from 'utils/icons/arrow-left-icon'
import { CheckIcon } from 'utils/icons/check-icon'
import { EditIcon } from 'utils/icons/edit-icon'
import { PlusIcon } from 'utils/icons/plus-icon'
import { XmarkIcon } from 'utils/icons/xmark-icon'

import BomAddForm from './BomAddForm'
import BomTable from './BomTable'

const ExpandedRowContainer = styled.div`
  padding: 0px;
  background-color: rgb(243, 244, 246);
  border-top: 1px solid #e5e7eb;
  border-bottom: 1px solid #e5e7eb;
  position: relative;
  min-height: 140px;
`

interface ExpandedRowComponentProps {
  data: LeadTimeRow
  onBomRefresh?: (productId: number | string) => void
  onBomAddSuccess?: () => void
}

const ExpandedRowComponent: React.FC<ExpandedRowComponentProps> = ({
  data,
  onBomRefresh,
  onBomAddSuccess,
}) => {
  const [showBomAddForm, setShowBomAddForm] = React.useState(false)

  if (!data.product_id) return null

  const handleBomRefresh = () => {
    if (data.product_id && onBomRefresh) {
      onBomRefresh(data.product_id)
    }
  }

  const handleBomAddSuccess = () => {
    setShowBomAddForm(false)
    if (onBomAddSuccess) {
      onBomAddSuccess()
    }
  }

  return (
    <ExpandedRowContainer>
      {data.isBomLoading && <PixelSpinner isShow={true} />}

      <DcFlexBox flexDirection="column" gap="16px" padding="20px">
        <DcFlexBox justifyContent="space-between" alignItems="center">
          <DcFlexBox height="auto" gap="8px">
            {showBomAddForm && (
              <ArrowLeftIcon
                color="black"
                size={20}
                onClick={() => setShowBomAddForm(false)}
              />
            )}
            <PixelText style={{ fontSize: '12px', fontWeight: 600 }}>
              BOM - {data.product_name || 'Product'}
            </PixelText>
          </DcFlexBox>
          {!showBomAddForm && (
            <DcButton
              variant="primary"
              size="ex-sm"
              onClick={() => setShowBomAddForm(true)}
              withIcon={true}
            >
              <PlusIcon color="white" size={16} />
              <span>Add Components</span>
            </DcButton>
          )}
        </DcFlexBox>

        {showBomAddForm ? (
          <BomAddForm
            productId={data.product_id}
            productSku={data.product_sku || 'tamplate'}
            onSuccess={handleBomAddSuccess}
          />
        ) : (
          <>
            {!data.isBomLoading && data.bomError && (
              <DcText style={{ fontSize: '13px', color: '#b91c1c' }}>
                {data.bomError}
              </DcText>
            )}
            {!data.isBomLoading &&
              !data.bomError &&
              (!data.bomItems || data.bomItems.length === 0) && (
                <DcText style={{ fontSize: '13px', color: '#6b7280' }}>
                  No BOM components found for this product. Click &quot;Add
                  Components&quot; to get started.
                </DcText>
              )}
            {!data.isBomLoading &&
              !data.bomError &&
              data.bomItems &&
              data.bomItems.length > 0 && (
                <BomTable
                  items={data.bomItems}
                  onRefresh={handleBomRefresh}
                  productId={data.product_id}
                />
              )}
          </>
        )}
      </DcFlexBox>
    </ExpandedRowContainer>
  )
}

interface LeadTimeTableProps {
  rows: LeadTimeRow[]
  onChangeRow: (index: number, row: LeadTimeRow) => void
  onUpdateSuccess?: () => void
  onRowClick?: (row: LeadTimeRow) => void
  onRowExpandToggled?: (row: LeadTimeRow, isExpanded: boolean) => void
  onBomRefresh?: (productId: number | string) => void
  onBomAddSuccess?: () => void
  isLoading?: boolean
  productType: string
}

const LeadTimeTable: React.FC<LeadTimeTableProps> = ({
  rows,
  onChangeRow,
  onUpdateSuccess,
  onRowClick,
  onRowExpandToggled,
  onBomRefresh,
  onBomAddSuccess,
  isLoading,
  productType,
}) => {
  const [editingId, setEditingId] = React.useState<number | null>(null)
  const [editValues, setEditValues] = React.useState<{
    lead_time?: number
    moq?: number
  }>({})
  const [isUpdating, setIsUpdating] = React.useState(false)

  const handleEdit = (row: LeadTimeRow) => {
    if (!row.id) return
    setEditingId(row.id)
    setEditValues({
      lead_time: row.lead_time,
      moq: row.moq,
    })
  }

  const handleCancel = () => {
    setEditingId(null)
    setEditValues({})
  }

  const handleSave = (row: LeadTimeRow, index: number) => {
    if (!row.id) return

    // Always send both fields, using edited values or current row values
    const newLeadTime =
      editValues.lead_time !== undefined
        ? editValues.lead_time
        : row.lead_time ?? 0
    const newMoq = editValues.moq !== undefined ? editValues.moq : row.moq ?? 0

    // Only send product_lead_time and product_moq in the payload
    const updatePayload = {
      product_lead_time: newLeadTime,
      product_moq: newMoq,
    }

    setIsUpdating(true)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    updateLeadTime(row.id, updatePayload as any).subscribe({
      next: (_res: AjaxResponse<BulkUploadResponse>) => {
        // Update the row in the table with new values
        const finalRow: LeadTimeRow = {
          ...row,
          lead_time: newLeadTime,
          moq: newMoq,
          new_lead_time: undefined,
          new_moq: undefined,
        }
        onChangeRow(index, finalRow)
        setEditingId(null)
        setEditValues({})
        setIsUpdating(false)
        onUpdateSuccess?.()
      },
      error: (err: AjaxError) => {
        console.error('lead-time-update:error', err)
        setIsUpdating(false)
      },
    })
  }

  const columns = [
    {
      dataKey: 'product_name',
      name: 'Product Name',
      sortable: true,
      selector: (row: LeadTimeRow) => (
        <DcFlexBox height="auto" gap="5px">
          <PixelImage
            src={row.product_image_path}
            alt={','}
            width="32px"
            height="32px"
          />
          <PixelText multiLine>{row.product_name || '--'}</PixelText>
        </DcFlexBox>
      ),
    },
    {
      dataKey: 'product_sku',
      name: 'Product SKU',
      sortable: true,
      selector: (row: LeadTimeRow) => row.product_sku || '--',
    },
    {
      dataKey: 'product_buffer_stock',
      name: 'Buffer Stock',
      sortable: true,
      selector: (row: LeadTimeRow) => row.product_buffer_stock || '--',
    },

    {
      dataKey: 'lead_time',
      name: 'Lead Time',
      sortable: true,
      selector: (row: LeadTimeRow) => {
        const isEditing = row.id === editingId
        if (isEditing) {
          return (
            <input
              type="number"
              min={0}
              style={{
                width: '100%',
                padding: '4px 8px',
                borderRadius: 6,
                border: '1px solid #d1d5db',
                fontSize: 12,
              }}
              value={
                typeof editValues.lead_time === 'number'
                  ? editValues.lead_time
                  : ''
              }
              onChange={(e) => {
                const value = e.target.value
                setEditValues((prev) => ({
                  ...prev,
                  lead_time:
                    value === '' ? undefined : Math.max(0, Number(value) || 0),
                }))
              }}
            />
          )
        }
        return typeof row.lead_time === 'number' ? row.lead_time : '--'
      },
    },
    {
      dataKey: 'moq',
      name: 'MOQ',
      sortable: true,
      selector: (row: LeadTimeRow) => {
        const isEditing = row.id === editingId
        if (isEditing) {
          return (
            <input
              type="number"
              min={0}
              style={{
                width: '100%',
                padding: '4px 8px',
                borderRadius: 6,
                border: '1px solid #d1d5db',
                fontSize: 12,
              }}
              value={typeof editValues.moq === 'number' ? editValues.moq : ''}
              onChange={(e) => {
                const value = e.target.value
                setEditValues((prev) => ({
                  ...prev,
                  moq:
                    value === '' ? undefined : Math.max(0, Number(value) || 0),
                }))
              }}
            />
          )
        }
        return typeof row.moq === 'number' ? row.moq : '--'
      },
    },
    {
      dataKey: 'actions',
      name: 'Actions',
      width: '140px',
      sortable: false,
      selector: (row: LeadTimeRow, index: number) => {
        const isEditing = row.id === editingId

        if (isEditing) {
          return (
            <DcFlexBox gap="8px" justifyContent="center">
              <CheckIcon
                color="#28a745"
                size={20}
                style={{ cursor: 'pointer' }}
                onClick={() => handleSave(row, index)}
              />
              <XmarkIcon
                color="#dc3545"
                size={20}
                style={{ cursor: 'pointer' }}
                onClick={handleCancel}
              />
            </DcFlexBox>
          )
        }

        return (
          <DcFlexBox justifyContent="center" gap="8px">
            <EditIcon
              color="#5c4dff"
              size={20}
              style={{ cursor: 'pointer' }}
              onClick={() => handleEdit(row)}
            />
          </DcFlexBox>
        )
      },
    },
  ]

  return (
    <PixelTable
      columns={columns}
      textSize="default"
      data={rows || []}
      isSearchable={true}
      pointerOnHover
      onRowClicked={onRowClick}
      fixedHeader
      fixedHeaderScrollHeight="calc(100vh - 275px)"
      pagination={rows.length > 25}
      paginationPerPage={25}
      progressPending={isUpdating || isLoading}
      // Only allow expandable rows for Finished Goods
      expandableRows={productType === 'FG'}
      expandableRowsComponent={(props: { data: LeadTimeRow }) => (
        <ExpandedRowComponent
          data={props.data}
          onBomRefresh={onBomRefresh}
          onBomAddSuccess={onBomAddSuccess}
        />
      )}
      expandableRowsHideExpander={productType !== 'FG'}
      expandableRowExpanded={(row: LeadTimeRow) => Boolean(row.isExpanded)}
      expandOnRowClicked={false}
      onRowExpandToggled={(expanded: boolean, row: LeadTimeRow) => {
        // Guard expansion based on product type
        if (productType !== 'FG') {
          return
        }
        if (onRowExpandToggled) {
          onRowExpandToggled(row, expanded)
        }
      }}
    />
  )
}

export default LeadTimeTable
